import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import { MoreHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";

const fabButtonSizeClassName = "h-11 w-11";
const fabIconSizeClassName = "h-5 w-5";

export type SpeedDialAction = {
  id: string;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

interface MobileSpeedDialProps {
  actions: SpeedDialAction[];
  className?: string;
}

export function MobileSpeedDial({ actions, className }: MobileSpeedDialProps) {
  const [open, setOpen] = useState(false);

  if (actions.length === 0) return null;

  const handleAction = (action: SpeedDialAction) => {
    if (action.disabled) return;
    setOpen(false);
    action.onClick();
  };

  return (
    <div className={cn("sm:hidden", className)}>
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-[rgba(15,23,42,0.25)] dark:bg-black/40"
          aria-label="Close actions menu"
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-40 flex flex-col-reverse items-end gap-2">
        {open
          ? actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  type="button"
                  title={action.label}
                  aria-label={action.label}
                  disabled={action.disabled}
                  onClick={() => handleAction(action)}
                  className={cn(
                    "flex items-center justify-center rounded-xl border border-[var(--lm-border-subtle)] bg-white text-[var(--lm-foreground-strong)] shadow-md transition-transform active:scale-95 dark:border-white/10 dark:bg-slate-800 dark:text-white",
                    fabButtonSizeClassName,
                    action.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <Icon className={fabIconSizeClassName} />
                </button>
              );
            })
          : null}

        <button
          type="button"
          aria-label={open ? "Close actions menu" : "Open actions menu"}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className={cn(
            "flex items-center justify-center rounded-xl bg-[#9333F5] text-white shadow-md shadow-primary/20 transition-transform hover:scale-105 active:scale-95",
            fabButtonSizeClassName,
          )}
        >
          {open ? <X className={fabIconSizeClassName} /> : <MoreHorizontal className={fabIconSizeClassName} />}
        </button>
      </div>
    </div>
  );
}
